import { useState, useEffect } from 'react';
import { animate, stagger } from 'animejs';
import { PortableText } from '@portabletext/react';
import { useProjects } from '../hooks/useProjects';
import { useSideProjects } from '../hooks/useSideProjects';
import { urlFor } from '../sanity/client';

// Factory function to create Portable Text components with video expand handler
const createPortableTextComponents = (onVideoClick) => ({
  block: {
    h2: ({ children }) => <h2 className="text-base font-medium text-neutral-900 mt-8 mb-3">{children}</h2>,
    h3: ({ children }) => <h3 className="text-base font-medium text-neutral-900 mt-6 mb-2">{children}</h3>,
    blockquote: ({ children }) => <blockquote className="border-l-2 border-neutral-300 pl-4 italic text-neutral-600 my-4">{children}</blockquote>,
    normal: ({ children }) => <p className="text-sm text-neutral-600 leading-relaxed mb-4">{children}</p>,
  },
  marks: {
    strong: ({ children }) => <strong className="font-medium text-neutral-900">{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    underline: ({ children }) => <span className="underline">{children}</span>,
    code: ({ children }) => <code className="bg-neutral-100 px-1.5 py-0.5 rounded text-xs font-mono text-neutral-800">{children}</code>,
    link: ({ value, children }) => (
      <a href={value?.href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline bg-blue-50 px-0.5 rounded hover:bg-blue-100 transition-colors">
        {children}
      </a>
    ),
  },
  types: {
    image: ({ value }) => (
      <figure className="my-4">
        <img src={value.url} alt={value.alt || ''} className="w-full rounded-lg" />
        {value.caption && <figcaption className="text-xs text-neutral-500 mt-2 text-center">{value.caption}</figcaption>}
      </figure>
    ),
    codeBlock: ({ value }) => (
      <pre className="bg-neutral-900 text-neutral-100 p-4 rounded-lg overflow-x-auto my-4 text-sm">
        <code className={`language-${value.language || 'plain'}`}>{value.code}</code>
      </pre>
    ),
    imageCarousel: ({ value }) => (
      <div className="my-10">
        {value.caption && (
          <p className="text-xs text-neutral-500 mb-3 px-6">{value.caption}</p>
        )}
        <div 
          className="-mx-6 overflow-x-auto cursor-grab active:cursor-grabbing scrollbar-hide"
          style={{ scrollSnapType: 'x mandatory' }}
          onMouseDown={(e) => {
            const el = e.currentTarget;
            el.dataset.dragging = 'true';
            el.dataset.startX = e.pageX - el.offsetLeft;
            el.dataset.scrollLeft = el.scrollLeft;
          }}
          onMouseMove={(e) => {
            const el = e.currentTarget;
            if (el.dataset.dragging !== 'true') return;
            e.preventDefault();
            const x = e.pageX - el.offsetLeft;
            const walk = (x - Number(el.dataset.startX)) * 1.5;
            el.scrollLeft = Number(el.dataset.scrollLeft) - walk;
          }}
          onMouseUp={(e) => { e.currentTarget.dataset.dragging = 'false'; }}
          onMouseLeave={(e) => { e.currentTarget.dataset.dragging = 'false'; }}
        >
          <div className="flex gap-3 px-6" style={{ width: 'max-content' }}>
            {value.images?.map((img, i) => (
              <figure key={i} className="flex-shrink-0" style={{ scrollSnapAlign: 'start' }}>
                <img 
                  src={img.url} 
                  alt={img.alt || ''} 
                  className="h-auto max-h-[450px] rounded-lg object-cover"
                  style={{ maxWidth: 'min(400px, calc(100vw - 80px))' }}
                  draggable="false"
                />
                {img.caption && <figcaption className="text-xs text-neutral-500 mt-2 text-center">{img.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </div>
      </div>
    ),
    divider: ({ value }) => {
      const style = value?.style || 'line';
      if (style === 'dots') {
        return <div className="my-8 text-center text-neutral-300">• • •</div>;
      }
      if (style === 'space') {
        return <div className="my-12" />;
      }
      return <hr className="my-8 border-t border-neutral-200" />;
    },
    video: ({ value }) => (
      <figure className="my-6 group relative">
        <video
          src={value.url}
          autoPlay
          muted
          loop
          playsInline
          className="w-full rounded-lg md:cursor-pointer"
          onClick={() => {
            // Only expand on desktop (md and up)
            if (window.innerWidth >= 768) {
              onVideoClick({ url: value.url, caption: value.caption });
            }
          }}
        />
        {/* Expand hint overlay - desktop only */}
        <div 
          className="hidden md:flex absolute inset-0 items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors rounded-lg cursor-pointer"
          onClick={() => onVideoClick({ url: value.url, caption: value.caption })}
        >
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 px-3 py-1.5 rounded-full text-xs text-neutral-700 font-medium shadow-sm">
            Click to expand
          </div>
        </div>
        {value.caption && <figcaption className="text-xs text-neutral-500 mt-2 text-center">{value.caption}</figcaption>}
      </figure>
    ),
  },
  list: {
    bullet: ({ children }) => <ul className="list-disc list-outside ml-4 text-sm text-neutral-600 space-y-1 mb-3">{children}</ul>,
    number: ({ children }) => <ol className="list-decimal list-outside ml-4 text-sm text-neutral-600 space-y-1 mb-3">{children}</ol>,
  },
});

const PortfolioV3 = ({ settings }) => {
  const { selectedProjects, loading } = useProjects();
  const { sideProjects, loading: sideProjectsLoading } = useSideProjects();
  const [selectedItem, setSelectedItem] = useState(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [fontMode, setFontMode] = useState(() => localStorage.getItem('fontMode') || 'modern');
  const [expandedVideo, setExpandedVideo] = useState(null);

  const featuredCount = selectedProjects.length;
  const isPanelOpen = selectedItem !== null;
  const fontFamily = fontMode === 'classic' ? '"PP Neue York", serif' : '"Pretendard Variable", sans-serif';
  const fontWeight = fontMode === 'classic' ? 400 : 500;

  const toggleFont = () => {
    const newMode = fontMode === 'modern' ? 'classic' : 'modern';
    setFontMode(newMode);
    localStorage.setItem('fontMode', newMode);
  };

  // Create portable text components with video click handler
  const portableTextComponents = createPortableTextComponents(setExpandedVideo);

  // Close expanded video on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && expandedVideo) {
        setExpandedVideo(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expandedVideo]);

  // Staggered entrance animation for rows
  useEffect(() => {
    if (!loading && !sideProjectsLoading && !hasAnimated) {
      // Animate featured project rows
      animate('.project-row', {
        opacity: [0, 1],
        translateY: [8, 0],
        delay: stagger(15),
        duration: 150,
        ease: 'outExpo'
      });

      // Animate side project rows
      animate('.side-project-row', {
        opacity: [0, 1],
        translateY: [8, 0],
        delay: stagger(15, { start: 60 }),
        duration: 150,
        ease: 'outExpo'
      });

      setHasAnimated(true);
    }
  }, [loading, sideProjectsLoading, hasAnimated]);

  if (loading || sideProjectsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF9' }}>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#FAFAF9', fontFamily, fontWeight }}>
      {/* Main Content - only pushes on xl screens, overlays on lg */}
      <main className={`flex-1 transition-all duration-300 ${isPanelOpen ? 'xl:mr-[560px]' : ''}`}>
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="flex flex-col lg:flex-row lg:gap-16">
            {/* Left Column - Intro */}
            <aside className="lg:w-80 lg:flex-shrink-0 mb-12 lg:mb-0">
              <div className="lg:sticky lg:top-24">
                <div className="mb-6">
                  <p className="text-neutral-700 text-sm leading-relaxed">
                    <em className="text-neutral-900">Peter Abu</em> is a product designer with 6+ years of experience 
                   designing software and experiences. Currently based in London, United Kingdom. 
                    Find me on{' '}
                    <a 
                      href="https://layers.to/abu" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 underline bg-blue-50 px-0.5 rounded hover:bg-blue-100 transition-colors"
                    >
                      Layers
                    </a>
                    ,{' '}
                    <a 
                      href="https://linkedin.com/in/peteabu" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 underline bg-blue-50 px-0.5 rounded hover:bg-blue-100 transition-colors"
                    >
                      LinkedIn
                    </a>
                    {' '}and{' '}
                    <a 
                      href="https://github.com/peteabu" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 underline bg-blue-50 px-0.5 rounded hover:bg-blue-100 transition-colors"
                    >
                      Github
                    </a>
                    . View my{' '}
                    <a 
                      href="https://www.dropbox.com/scl/fi/z4rzwpjvr1ds7mufos20r/Resume_2.pdf?rlkey=7ck4s7rotm2soe6bobch5fsm8&st=4c1jc1ax&dl=0" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 underline bg-blue-50 px-0.5 rounded hover:bg-blue-100 transition-colors"
                    >
                      Resume
                    </a>
                    {' '}or{' '}
                    <a 
                      href="mailto:pabu@hey.com"
                      className="text-blue-600 underline bg-blue-50 px-0.5 rounded hover:bg-blue-100 transition-colors"
                    >
                      get in touch
                    </a>
                    .
                  </p>
                </div>

                <p className="text-neutral-500 text-xs mb-1">
                  {featuredCount} featured projects
                </p>
                
                <p className="text-neutral-500 text-xs mb-2">
                  Last updated {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>

                <p className="text-neutral-500 text-xs mb-8 lg:mb-0">
                  To see all my projects, view the{' '}
                  <a href="/timeline" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline bg-blue-50 px-0.5 rounded hover:bg-blue-100 transition-colors">Timeline</a>
                </p>

                <footer className="hidden lg:block mt-12 pt-8 border-t border-neutral-200">
                  <p className="text-neutral-500 text-xs">© {new Date().getFullYear()} Peter Abu.</p>
                  <p className="text-neutral-500 text-xs mt-1">Designed by me, built with Kiro and plenty of credits.</p>
                  <button
                    onClick={() => { toggleFont(); navigator.vibrate?.(10); }}
                    className="text-xs text-neutral-500 hover:text-neutral-900 transition-colors mt-3"
                  >
                    View in {fontMode === 'modern' ? 'Classic' : 'Modern'} type
                  </button>
                </footer>
              </div>
            </aside>

            {/* Right Column - Tables */}
            <div className="flex-1 min-w-0">
              {/* Featured Design Work */}
              <h2 className="text-neutral-900 text-base font-medium mb-4">Featured Design Work</h2>
              
              <div className="grid grid-cols-[20px_1fr_50px] md:grid-cols-[32px_1fr_60px] gap-2 md:gap-4 text-xs text-neutral-500 mb-4 px-2">
                <span>#</span>
                <span>Project</span>
                <span className="text-right">Year</span>
              </div>
              <div className="border-t border-neutral-200 mb-2" />

              <div className="space-y-0">
                {selectedProjects.map((project, index) => {
                  const baseYear = 2018;
                  const year = baseYear + Math.floor(project.monthIndex / 12);
                  const isSelected = selectedItem?.type === 'project' && selectedItem?.data._id === project._id;
                  
                  return (
                    <button
                      key={project._id}
                      onClick={() => setSelectedItem({ type: 'project', data: project })}
                      className={`project-row w-full grid grid-cols-[20px_1fr_50px] md:grid-cols-[32px_1fr_60px] gap-2 md:gap-4 items-center py-3 px-2 rounded-lg transition-colors text-left group opacity-0 ${
                        isSelected ? 'bg-neutral-100' : 'hover:bg-neutral-100'
                      }`}
                    >
                      <span className="text-neutral-500 text-xs">{index + 1}</span>
                      <div className="flex items-center gap-3 min-w-0">
                        {project.image ? (
                          project.imageUrl?.includes('.svg') ? (
                            <img 
                              src={project.imageUrl} 
                              alt={project.title} 
                              className="w-10 h-10 object-contain rounded flex-shrink-0" 
                            />
                          ) : (
                            <img 
                              src={urlFor(project.image).width(40).height(40).fit('max').auto('format').url()} 
                              srcSet={`${urlFor(project.image).width(40).height(40).fit('max').auto('format').url()} 1x, ${urlFor(project.image).width(80).height(80).fit('max').auto('format').url()} 2x, ${urlFor(project.image).width(120).height(120).fit('max').auto('format').url()} 3x`}
                              alt={project.title} 
                              className="w-10 h-10 object-contain rounded flex-shrink-0" 
                            />
                          )
                        ) : (
                          <div className="w-10 h-10 bg-neutral-200 rounded flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="text-neutral-900 text-sm font-medium truncate">{project.title}</p>
                          <p className="text-neutral-500 text-xs truncate">{project.description || 'Product Design'}</p>
                        </div>
                      </div>
                      <span className="text-neutral-500 text-xs text-right">{year}</span>
                    </button>
                  );
                })}
              </div>

              {selectedProjects.length === 0 && (
                <div className="text-center text-neutral-500 py-12">
                  <p className="text-sm">No projects yet</p>
                </div>
              )}

              {/* Side Projects */}
              <div className="mt-16">
                <h2 className="text-neutral-900 text-base font-medium mb-4">Side Projects</h2>
                
                <div className="grid grid-cols-[20px_1fr] md:grid-cols-[32px_1fr] gap-2 md:gap-4 text-xs text-neutral-500 mb-4 px-2">
                  <span>#</span>
                  <span>Project</span>
                </div>
                <div className="border-t border-neutral-200 mb-2" />

                <div className="space-y-0">
                  {sideProjects.map((project, index) => {
                    const isSelected = selectedItem?.type === 'sideProject' && selectedItem?.data._id === project._id;
                    
                    return (
                      <button
                        key={project._id}
                        onClick={() => setSelectedItem({ type: 'sideProject', data: project })}
                        className={`side-project-row w-full grid grid-cols-[20px_1fr] md:grid-cols-[32px_1fr] gap-2 md:gap-4 items-center py-3 px-2 rounded-lg transition-colors text-left group opacity-0 ${
                          isSelected ? 'bg-neutral-100' : 'hover:bg-neutral-100'
                        }`}
                      >
                        <span className="text-neutral-500 text-xs">{index + 1}</span>
                        <div className="flex items-center gap-3 min-w-0">
                          {project.image ? (
                            project.imageUrl?.includes('.svg') ? (
                              <img 
                                src={project.imageUrl} 
                                alt={project.title} 
                                className="w-10 h-10 object-contain rounded flex-shrink-0" 
                              />
                            ) : (
                              <img 
                                src={urlFor(project.image).width(40).height(40).fit('max').auto('format').url()} 
                                srcSet={`${urlFor(project.image).width(40).height(40).fit('max').auto('format').url()} 1x, ${urlFor(project.image).width(80).height(80).fit('max').auto('format').url()} 2x, ${urlFor(project.image).width(120).height(120).fit('max').auto('format').url()} 3x`}
                                alt={project.title} 
                                className="w-10 h-10 object-contain rounded flex-shrink-0" 
                              />
                            )
                          ) : (
                            <div className="w-10 h-10 bg-neutral-200 rounded flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-neutral-900 text-sm font-medium truncate">{project.title}</p>
                            <p className="text-neutral-500 text-xs truncate">{project.description || 'Side project'}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {sideProjects.length === 0 && (
                  <div className="text-center text-neutral-500 py-8">
                    <p className="text-xs">No side projects yet</p>
                  </div>
                )}
              </div>

              <footer className="lg:hidden mt-16 pt-8 border-t border-neutral-200">
                <p className="text-neutral-500 text-xs">© {new Date().getFullYear()} Peter Abu.</p>
                <p className="text-neutral-500 text-xs mt-1">Designed by me, built with Kiro and plenty of credits.</p>
                <button
                  onClick={() => { toggleFont(); navigator.vibrate?.(10); }}
                  className="text-xs text-neutral-500 hover:text-neutral-900 transition-colors mt-3"
                >
                  View in {fontMode === 'modern' ? 'Classic' : 'Modern'} type
                </button>
              </footer>
            </div>
          </div>
        </div>
      </main>

      {/* Detail Panel - pushes from right */}
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[560px] border-l border-neutral-200 z-50 transition-transform duration-300 ${
          isPanelOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ backgroundColor: '#FAFAF9' }}
      >
        {selectedItem && (
          <div className="h-full flex flex-col">
            {/* Panel Header - sticky */}
            <div className="sticky top-0 z-10 px-6 py-4 border-b border-neutral-200" style={{ backgroundColor: '#FAFAF9' }}>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setSelectedItem(null)}
                  className="flex items-center gap-2 text-xs text-neutral-600 hover:text-neutral-900 transition-colors"
                  aria-label="Close panel"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="hidden md:inline">Back</span>
                </button>
              </div>
            </div>

            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="max-w-lg mx-auto px-6 py-8">
                {/* Hero Image */}
                {selectedItem.data.image && (
                  selectedItem.data.imageUrl?.includes('.svg') ? (
                    <img
                      src={selectedItem.data.imageUrl}
                      alt={selectedItem.data.title}
                      className="w-full object-contain rounded-lg mb-8"
                    />
                  ) : (
                    <img
                      src={urlFor(selectedItem.data.image).width(500).auto('format').url()}
                      srcSet={`${urlFor(selectedItem.data.image).width(500).auto('format').url()} 1x, ${urlFor(selectedItem.data.image).width(1000).auto('format').url()} 2x, ${urlFor(selectedItem.data.image).width(1500).auto('format').url()} 3x`}
                      alt={selectedItem.data.title}
                      className="w-full object-contain rounded-lg mb-8"
                    />
                  )
                )}

                {/* Title */}
                <h1 className="text-base font-medium text-neutral-900 mb-2">
                  {selectedItem.data.title}
                </h1>

                {/* Metadata row */}
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  {selectedItem.data.projectTypes?.length > 0 ? (
                    selectedItem.data.projectTypes.map((type, i) => (
                      <span key={i} className="text-xs text-neutral-600 px-2 py-1 rounded" style={{ backgroundColor: '#F5F5F4' }}>
                        {type}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-neutral-600 px-2 py-1 rounded" style={{ backgroundColor: '#F5F5F4' }}>Project</span>
                  )}
                  {selectedItem.type === 'project' && (
                    <span className="text-xs text-neutral-500 ml-2">
                      {2018 + Math.floor(selectedItem.data.monthIndex / 12)}
                    </span>
                  )}
                </div>

                {/* Rich Content */}
                <div className="mb-8">
                  {(selectedItem.type === 'project' && selectedItem.data.detailedDescription?.length > 0) ? (
                    <div className="prose-sm">
                      <PortableText value={selectedItem.data.detailedDescription} components={portableTextComponents} />
                    </div>
                  ) : (selectedItem.type === 'sideProject' && selectedItem.data.content?.length > 0) ? (
                    <div className="prose-sm">
                      <PortableText value={selectedItem.data.content} components={portableTextComponents} />
                    </div>
                  ) : (
                    <p className="text-neutral-600 text-sm leading-relaxed">
                      {selectedItem.data.description || 'No description available.'}
                    </p>
                  )}
                </div>

                {/* Links section */}
                {((selectedItem.type === 'sideProject' && selectedItem.data.links?.length > 0) ||
                  (selectedItem.type === 'project' && selectedItem.data.links?.length > 0)) && (
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {selectedItem.data.links.map((link, i) => (
                      <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 underline bg-blue-50 px-1.5 py-0.5 rounded hover:bg-blue-100 transition-colors"
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Overlay for mobile panel */}
      {isPanelOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 md:hidden"
          onClick={() => setSelectedItem(null)}
        />
      )}

      {/* Expanded Video Overlay - Desktop only */}
      {expandedVideo && (
        <>
          {/* Black overlay on main content area only (not sidepanel) */}
          <div 
            className="hidden md:block fixed top-0 left-0 h-full bg-black/70 z-[55]"
            style={{ right: '560px' }}
            onClick={() => setExpandedVideo(null)}
          />
          
          {/* Video container - positioned in main content area */}
          <div 
            className="hidden md:flex fixed top-1/2 left-0 -translate-y-1/2 z-[56] pl-8 items-center justify-center"
            style={{ right: '600px' }}
          >
            <div className="flex items-start gap-3 animate-in slide-in-from-right-8 fade-in duration-300">
              <div>
                <video
                  src={expandedVideo.url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="max-h-[80vh] w-auto rounded-xl"
                />
                {expandedVideo.caption && (
                  <p className="text-xs text-neutral-600 mt-3 text-center">{expandedVideo.caption}</p>
                )}
              </div>
              {/* Close button beside the video */}
              <button
                onClick={() => setExpandedVideo(null)}
                className="flex-shrink-0 mt-1 rounded-full p-2 bg-white text-neutral-500 hover:text-neutral-900 transition-colors shadow-md"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PortfolioV3;
